import { Injectable, inject } from '@angular/core';
import { distinctUntilChanged } from 'rxjs/operators';
import { PlaybackService } from './playback.service';
import { environment } from '../../../environments/environment';

// Instruments the Spotify Web Playback SDK's hidden media element to answer
// WHY audio stalls: was the buffer empty, and was the network reachable at
// that moment? The SDK is a DRM black box, but the underlying HTMLMediaElement
// still exposes native stall events ('waiting'/'stalled') and its real buffer
// level. Findings land in the console buffer (stall events, per-track summary)
// and in bug-report context via getDiagnostics().

export interface BufferSample {
  at: number;              // epoch ms
  currentTime: number;     // media element playhead, seconds
  bufferedAhead: number;   // seconds of audio buffered past the playhead
  readyState: number;      // HTMLMediaElement.readyState (4 = enough data)
  paused: boolean;
}

export interface StallRecord {
  at: number;              // epoch ms of the stall event
  type: string;            // 'waiting' | 'stalled'
  currentTime: number;
  bufferedAhead: number;
  readyState: number;
  connection: ConnectionSnapshot | null;
  backendPingMs: number | null;  // null = ping failed/timed out
  recoveredAfterMs: number | null; // filled when 'playing' fires again
}

export interface ConnectionSnapshot {
  effectiveType?: string;  // '4g' etc.
  downlink?: number;       // Mbps estimate
  rtt?: number;            // ms estimate
  saveData?: boolean;
}

interface TrackStallStats {
  trackName: string | null;
  startedAt: number;
  stallCount: number;
  totalStalledMs: number;
  minBufferedAhead: number | null; // lowest periodic sample seen
  sampleCount: number;
}

@Injectable({ providedIn: 'root' })
export class StallMonitorService {
  private playbackService = inject(PlaybackService);

  private started = false;
  private attached = new WeakSet<HTMLMediaElement>();
  private element: HTMLMediaElement | null = null;
  private openStall: StallRecord | null = null;

  private samples: BufferSample[] = [];
  private stalls: StallRecord[] = [];
  private readonly SAMPLE_INTERVAL_MS = 5000;
  private readonly SAMPLE_RING_SIZE = 60;   // ~5 minutes
  private readonly STALL_RING_SIZE = 20;
  private readonly PING_TIMEOUT_MS = 3000;

  private trackStats: TrackStallStats | null = null;

  public start(): void {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;

    // The SDK creates its media element around 'ready'; it can appear a beat
    // later and gets recreated on reconnect, so re-scan on every ready flip.
    this.playbackService.isReady$.subscribe(ready => {
      if (ready) this.scanSoon();
    });

    setInterval(() => this.samplePeriodic(), this.SAMPLE_INTERVAL_MS);

    // Per-track stall summary — turns "fails every other song" into a rate.
    this.playbackService.currentTrack$.pipe(
      distinctUntilChanged((a, b) => a?.id === b?.id)
    ).subscribe(track => {
      this.logTrackSummary();
      this.trackStats = track ? {
        trackName: track.name,
        startedAt: Date.now(),
        stallCount: 0,
        totalStalledMs: 0,
        minBufferedAhead: null,
        sampleCount: 0
      } : null;
    });
  }

  public getDiagnostics(): { mediaElementFound: boolean; samples: BufferSample[]; stalls: StallRecord[] } {
    return {
      mediaElementFound: !!this.element,
      samples: this.samples.map(s => ({ ...s })),
      stalls: this.stalls.map(s => ({ ...s, connection: s.connection ? { ...s.connection } : null }))
    };
  }

  private scanSoon(): void {
    // A few delayed scans — element creation isn't synchronous with 'ready'.
    for (const delay of [0, 2000, 5000]) {
      setTimeout(() => this.scanForMediaElement(), delay);
    }
  }

  private scanForMediaElement(): void {
    const candidates = Array.from(document.querySelectorAll<HTMLMediaElement>('audio, video'));
    for (const el of candidates) {
      if (this.attached.has(el)) continue;
      this.attached.add(el);
      this.element = el;
      this.attachListeners(el);
      console.log(`[stall] monitoring SDK media element (<${el.tagName.toLowerCase()}>, readyState ${el.readyState})`);
    }
    if (candidates.length > 0) {
      this.element = candidates[candidates.length - 1];
    }
  }

  private attachListeners(el: HTMLMediaElement): void {
    // 'waiting' = playback halted because the buffer ran dry — the user-audible
    // stall. 'stalled' = the network fetch stopped delivering (may not be
    // audible yet). 'playing' closes an open stall episode.
    el.addEventListener('waiting', () => void this.onStall(el, 'waiting'));
    el.addEventListener('stalled', () => void this.onStall(el, 'stalled'));
    el.addEventListener('playing', () => this.onPlaying());
  }

  private async onStall(el: HTMLMediaElement, type: string): Promise<void> {
    const record: StallRecord = {
      at: Date.now(),
      type,
      currentTime: +el.currentTime.toFixed(2),
      bufferedAhead: this.bufferedAhead(el),
      readyState: el.readyState,
      connection: this.connectionSnapshot(),
      backendPingMs: null,
      recoveredAfterMs: null
    };
    // Only 'waiting' halts audio — that's the episode worth timing.
    if (type === 'waiting' && !this.openStall) {
      this.openStall = record;
      if (this.trackStats) this.trackStats.stallCount++;
    }
    this.stalls.push(record);
    if (this.stalls.length > this.STALL_RING_SIZE) this.stalls.shift();

    const conn = record.connection;
    const connText = conn ? `${conn.effectiveType ?? '?'} downlink ${conn.downlink ?? '?'}Mbps rtt ${conn.rtt ?? '?'}ms` : 'no connection API';
    console.log(`[stall] ${type} at ${record.currentTime}s — buffer ${record.bufferedAhead}s ahead, readyState ${record.readyState}, ${connText}`);

    // Ping our own backend DURING the stall: fast answer = phone network fine,
    // starvation is Spotify/browser-side; timeout = genuine connectivity gap.
    record.backendPingMs = await this.pingBackend();
    console.log(`[stall] backend ping during ${type}: ${record.backendPingMs === null ? 'FAILED/timeout' : record.backendPingMs + 'ms'}`);
  }

  private onPlaying(): void {
    if (!this.openStall) return;
    const stalledMs = Date.now() - this.openStall.at;
    this.openStall.recoveredAfterMs = stalledMs;
    this.openStall = null;
    if (this.trackStats) this.trackStats.totalStalledMs += stalledMs;
    console.log(`[stall] recovered after ${stalledMs}ms`);
  }

  private samplePeriodic(): void {
    const el = this.element;
    if (!el || el.paused) return;
    const sample: BufferSample = {
      at: Date.now(),
      currentTime: +el.currentTime.toFixed(2),
      bufferedAhead: this.bufferedAhead(el),
      readyState: el.readyState,
      paused: el.paused
    };
    this.samples.push(sample);
    if (this.samples.length > this.SAMPLE_RING_SIZE) this.samples.shift();
    if (this.trackStats) {
      this.trackStats.sampleCount++;
      if (this.trackStats.minBufferedAhead === null || sample.bufferedAhead < this.trackStats.minBufferedAhead) {
        this.trackStats.minBufferedAhead = sample.bufferedAhead;
      }
    }
  }

  private logTrackSummary(): void {
    const stats = this.trackStats;
    if (!stats || !stats.trackName) return;
    const playedS = Math.round((Date.now() - stats.startedAt) / 1000);
    if (!this.element) {
      // The SDK plays inside a cross-origin iframe, so its media element is
      // usually invisible here and "0 stalls" would be a lie. Report the
      // SDK-side stall episodes the playback service detected instead.
      const episodes = this.playbackService.getRecoveryDiagnostics().stallEpisodes
        .filter(episode => episode.at >= stats.startedAt);
      if (episodes.length > 0) {
        const outcomes = episodes.map(episode => `${episode.outcome}${episode.resolvedAfterMs !== null ? ` ${episode.resolvedAfterMs}ms` : ''}`).join(', ');
        console.log(`[stall] track summary "${stats.trackName}": ${episodes.length} SDK stall(s) over ${playedS}s (${outcomes})`);
      }
      return;
    }
    console.log(
      `[stall] track summary "${stats.trackName}": ${stats.stallCount} stalls, ` +
      `${stats.totalStalledMs}ms stalled over ${playedS}s, ` +
      `min buffer ${stats.minBufferedAhead ?? 'n/a'}s (${stats.sampleCount} samples)`
    );
  }

  private bufferedAhead(el: HTMLMediaElement): number {
    try {
      const t = el.currentTime;
      for (let i = 0; i < el.buffered.length; i++) {
        if (el.buffered.start(i) <= t && t <= el.buffered.end(i)) {
          return +(el.buffered.end(i) - t).toFixed(2);
        }
      }
    } catch {
      // buffered ranges can throw on a detached element — treat as unknown.
    }
    return 0;
  }

  private connectionSnapshot(): ConnectionSnapshot | null {
    const conn = (navigator as Navigator & { connection?: ConnectionSnapshot }).connection;
    if (!conn) return null;
    return {
      effectiveType: conn.effectiveType,
      downlink: conn.downlink,
      rtt: conn.rtt,
      saveData: conn.saveData
    };
  }

  private async pingBackend(): Promise<number | null> {
    const startedAt = Date.now();
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), this.PING_TIMEOUT_MS);
      const response = await fetch(`${environment.apiUrl}/version`, {
        cache: 'no-store',
        credentials: 'include',
        signal: controller.signal
      });
      clearTimeout(timer);
      return response.ok ? Date.now() - startedAt : null;
    } catch {
      return null;
    }
  }
}
